import { Injectable } from '@angular/core';
import { AngularFirestore, AngularFirestoreCollection } from '@angular/fire/firestore';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { Account, Tenant, Voucher, VoucherResetPeriod } from 'src/models/account.model';
import { TenantService } from './tenant.service';

interface TenantSession {
  tenant_id: string;
  account_key: string;
  store_name: string;
  tenant_name: string;
  username: string;
  logged_in_at: Date;
  expires_at: Date;
}

@Injectable({
  providedIn: 'root'
})
export class VoucherService {

  private vouchersCollection: AngularFirestoreCollection<Voucher>;

  constructor(
    private afs: AngularFirestore,
    private tenantService: TenantService
  ) {
    this.vouchersCollection = this.afs.collection<Voucher>('vouchers');
  }

  // Validate tenant session from localStorage
  private validateTenantSession(account: Account, tenant: Tenant): TenantSession | null {
    const sessionStr = localStorage.getItem('tenant_session');
    if (!sessionStr) {
      return null;
    }

    try {
      const session: TenantSession = JSON.parse(sessionStr);

      // Verify session matches the account and tenant
      if (session.account_key !== account.account_key ||
        session.tenant_id !== tenant.tenant_id) {
        return null;
      }

      // Check if session has expired
      const expiresAt = new Date(session.expires_at);
      if (new Date() > expiresAt) {
        localStorage.removeItem('tenant_session');
        return null;
      }

      return session;
    } catch {
      return null;
    }
  }

  // Generate voucher code: 6-digit numeric code using timestamp + random for uniqueness
  generateVoucherCode(): string {
    const timestamp = Date.now();
    // Last 3 digits of timestamp (changes every millisecond)
    const timePart = (timestamp % 1000).toString().padStart(3, '0');
    // 3 random digits
    const randomPart = Math.floor(Math.random() * 1000).toString().padStart(3, '0');
    return timePart + randomPart;
  }

  // Get the start date for the current reset period (daily reset only)
  getPeriodStartDate(resetPeriod: VoucherResetPeriod): Date | null {
    if (!resetPeriod || resetPeriod === 'none') {
      return null; // No period filter - count all vouchers
    }

    if (resetPeriod === 'daily') {
      // Start of today (midnight)
      const now = new Date();
      return new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0, 0);
    }

    return null;
  }

  // Check if tenant can generate vouchers (enforces limit)
  private async checkGenerationLimit(tenant: Tenant, quantity: number = 1): Promise<{ allowed: boolean; reason?: string }> {
    const settings = tenant.voucher_settings;
    if (!settings) return { allowed: true };

    if (settings.generation_limit !== null && settings.generation_limit !== undefined) {
      // Get actual count from Firebase for accurate limit checking (with period filtering)
      const actualCount = await this.getVoucherCountForPeriod(tenant.tenant_id, settings.reset_period);
      const remaining = settings.generation_limit - actualCount;

      if (remaining <= 0) {
        return {
          allowed: false,
          reason: 'Voucher generation limit reached. You cannot generate more vouchers.'
        };
      }
      if (remaining < quantity) {
        return {
          allowed: false,
          reason: `Cannot generate ${quantity} voucher(s). Only ${remaining} remaining.`
        };
      }
    }
    return { allowed: true };
  }

  // Generate single voucher
  async generateVoucher(account: Account, tenant: Tenant): Promise<Voucher> {
    // Verify tenant session is valid
    const session = this.validateTenantSession(account, tenant);
    if (!session) {
      throw new Error('Tenant session invalid or expired. Please log in again.');
    }

    // Check generation limit before generating (uses actual count from Firebase)
    const canGenerate = await this.checkGenerationLimit(tenant, 1);
    if (!canGenerate.allowed) {
      throw new Error(canGenerate.reason);
    }

    const now = new Date();
    const expirationHours = tenant.voucher_settings?.expiration_hours || 24;
    const expiresAt = new Date(now.getTime() + (expirationHours * 60 * 60 * 1000));

    const voucher: Voucher = {
      voucher_id: this.afs.createId(),
      voucher_code: this.generateVoucherCode(),
      account_key: account.account_key,
      tenant_id: tenant.tenant_id,
      tenant_name: tenant.tenant_name,
      store_name: tenant.store_name,
      free_parking_hours: tenant.voucher_settings?.free_parking_hours || 1,
      status: 'active',
      generated_at: now,
      expires_at: expiresAt
    };

    await this.vouchersCollection.doc(voucher.voucher_id).set(voucher);
    await this.tenantService.incrementVoucherCount(account, tenant.tenant_id, 1);

    return voucher;
  }

  // Generate batch vouchers
  async generateBatchVouchers(
    account: Account,
    tenant: Tenant,
    quantity: number
  ): Promise<Voucher[]> {
    // Verify tenant session is valid
    const session = this.validateTenantSession(account, tenant);
    if (!session) {
      throw new Error('Tenant session invalid or expired. Please log in again.');
    }

    // Check generation limit before generating (uses actual count from Firebase)
    const canGenerate = await this.checkGenerationLimit(tenant, quantity);
    if (!canGenerate.allowed) {
      throw new Error(canGenerate.reason);
    }

    const batchId = this.afs.createId();
    const vouchers: Voucher[] = [];
    const now = new Date();
    const expirationHours = tenant.voucher_settings?.expiration_hours || 24;

    for (let i = 0; i < quantity; i++) {
      const expiresAt = new Date(now.getTime() + (expirationHours * 60 * 60 * 1000));

      const voucher: Voucher = {
        voucher_id: this.afs.createId(),
        voucher_code: this.generateVoucherCode(),
        account_key: account.account_key,
        tenant_id: tenant.tenant_id,
        tenant_name: tenant.tenant_name,
        store_name: tenant.store_name,
        free_parking_hours: tenant.voucher_settings?.free_parking_hours || 1,
        status: 'active',
        generated_at: now,
        expires_at: expiresAt,
        batch_id: batchId
      };

      vouchers.push(voucher);
    }

    // Batch write to Firestore
    const batch = this.afs.firestore.batch();
    vouchers.forEach(v => {
      const docRef = this.afs.collection('vouchers').doc(v.voucher_id).ref;
      batch.set(docRef, v);
    });
    await batch.commit();

    await this.tenantService.incrementVoucherCount(account, tenant.tenant_id, quantity);

    return vouchers;
  }

  // Get vouchers by tenant
  getVouchersByTenant(tenantId: string): Observable<Voucher[]> {
    return this.afs.collection<Voucher>('vouchers', ref =>
      ref.where('tenant_id', '==', tenantId)
        .orderBy('generated_at', 'desc')
    ).snapshotChanges().pipe(
      map(actions => {
        return actions.map(a => {
          const data = a.payload.doc.data() as Voucher;
          const id = a.payload.doc.id;
          return { ...data, voucher_id: id };
        });
      })
    );
  }

  // Get vouchers by account
  getVouchersByAccount(accountKey: string): Observable<Voucher[]> {
    return this.afs.collection<Voucher>('vouchers', ref =>
      ref.where('account_key', '==', accountKey)
        .orderBy('generated_at', 'desc')
    ).snapshotChanges().pipe(
      map(actions => {
        return actions.map(a => {
          const data = a.payload.doc.data() as Voucher;
          const id = a.payload.doc.id;
          return { ...data, voucher_id: id };
        });
      })
    );
  }

  // Get voucher by code
  async getVoucherByCode(voucherCode: string): Promise<Voucher | null> {
    const snapshot = await this.afs.collection<Voucher>('vouchers', ref =>
      ref.where('voucher_code', '==', voucherCode).limit(1)
    ).get().toPromise();

    if (snapshot.empty) {
      return null;
    }

    return snapshot.docs[0].data() as Voucher;
  }

  // Validate and redeem voucher
  async redeemVoucher(voucherCode: string, plateNumber: string, logId: string): Promise<{
    success: boolean;
    voucher?: Voucher;
    error?: string;
  }> {
    const snapshot = await this.afs.collection<Voucher>('vouchers', ref =>
      ref.where('voucher_code', '==', voucherCode).limit(1)
    ).get().toPromise();

    if (snapshot.empty) {
      return { success: false, error: 'Voucher not found' };
    }

    const doc = snapshot.docs[0];
    const voucher = doc.data() as Voucher;

    if (voucher.status === 'used') {
      return { success: false, error: 'Voucher already used' };
    }

    const now = new Date();
    const expiresAt = voucher.expires_at instanceof Date
      ? voucher.expires_at
      : new Date((voucher.expires_at as any).seconds * 1000);

    if (voucher.status === 'expired' || now > expiresAt) {
      await this.afs.collection('vouchers').doc(doc.id).update({ status: 'expired' });
      return { success: false, error: 'Voucher expired' };
    }

    // Mark as used
    await this.afs.collection('vouchers').doc(doc.id).update({
      status: 'used',
      used_at: now,
      used_by_plate_number: plateNumber,
      used_by_log_id: logId
    });

    return { success: true, voucher: { ...voucher, status: 'used', used_at: now } };
  }

  // Update voucher status
  async updateVoucherStatus(voucherId: string, status: 'active' | 'used' | 'expired'): Promise<void> {
    await this.vouchersCollection.doc(voucherId).update({ status });
  }

  // Check and update expired vouchers for a tenant (updates Firebase)
  async checkAndUpdateExpiredVouchers(tenantId: string): Promise<number> {
    const now = new Date();
    const snapshot = await this.afs.collection<Voucher>('vouchers', ref =>
      ref.where('tenant_id', '==', tenantId)
        .where('status', '==', 'active')
    ).get().toPromise();

    let expiredCount = 0;

    if (!snapshot.empty) {
      const batch = this.afs.firestore.batch();

      snapshot.docs.forEach(doc => {
        const voucher = doc.data() as Voucher;
        const expiresAt = voucher.expires_at instanceof Date
          ? voucher.expires_at
          : new Date((voucher.expires_at as any).seconds * 1000);

        if (now > expiresAt) {
          batch.update(doc.ref, { status: 'expired' });
          expiredCount++;
        }
      });

      if (expiredCount > 0) {
        await batch.commit();
      }
    }

    return expiredCount;
  }

  // Check voucher status by code and update if expired
  async checkVoucherStatus(voucherCode: string): Promise<{
    voucher: Voucher | null;
    statusUpdated: boolean;
    message: string;
  }> {
    const snapshot = await this.afs.collection<Voucher>('vouchers', ref =>
      ref.where('voucher_code', '==', voucherCode).limit(1)
    ).get().toPromise();

    if (snapshot.empty) {
      return { voucher: null, statusUpdated: false, message: 'Voucher not found' };
    }

    const doc = snapshot.docs[0];
    const voucher = doc.data() as Voucher;

    // If already used, return as is
    if (voucher.status === 'used') {
      return { voucher, statusUpdated: false, message: 'Voucher has already been used' };
    }

    // If already expired, return as is
    if (voucher.status === 'expired') {
      return { voucher, statusUpdated: false, message: 'Voucher has expired' };
    }

    // Check if active voucher has expired
    const now = new Date();
    const expiresAt = voucher.expires_at instanceof Date
      ? voucher.expires_at
      : new Date((voucher.expires_at as any).seconds * 1000);

    if (now > expiresAt) {
      // Update to expired in Firebase
      await this.afs.collection('vouchers').doc(doc.id).update({ status: 'expired' });
      return {
        voucher: { ...voucher, status: 'expired' },
        statusUpdated: true,
        message: 'Voucher has expired'
      };
    }

    return { voucher, statusUpdated: false, message: 'Voucher is active and valid' };
  }

  // Expire old vouchers (can be called by cron or manually)
  async expireOldVouchers(): Promise<number> {
    const now = new Date();
    const snapshot = await this.afs.collection<Voucher>('vouchers', ref =>
      ref.where('status', '==', 'active')
    ).get().toPromise();

    let expiredCount = 0;

    if (!snapshot.empty) {
      const batch = this.afs.firestore.batch();

      snapshot.docs.forEach(doc => {
        const voucher = doc.data() as Voucher;
        const expiresAt = voucher.expires_at instanceof Date
          ? voucher.expires_at
          : new Date((voucher.expires_at as any).seconds * 1000);

        if (now > expiresAt) {
          batch.update(doc.ref, { status: 'expired' });
          expiredCount++;
        }
      });

      if (expiredCount > 0) {
        await batch.commit();
      }
    }

    return expiredCount;
  }

  // Get actual voucher count for tenant from Firebase
  async getVoucherCount(tenantId: string): Promise<number> {
    const snapshot = await this.afs.collection<Voucher>('vouchers', ref =>
      ref.where('tenant_id', '==', tenantId)
    ).get().toPromise();

    return snapshot.empty ? 0 : snapshot.docs.length;
  }

  // Get voucher count for tenant within the current reset period
  async getVoucherCountForPeriod(tenantId: string, resetPeriod?: VoucherResetPeriod): Promise<number> {
    const periodStart = this.getPeriodStartDate(resetPeriod);

    // If no period filter (lifetime), return total count
    if (!periodStart) {
      return this.getVoucherCount(tenantId);
    }

    // Query vouchers generated after the period start date
    const snapshot = await this.afs.collection<Voucher>('vouchers', ref =>
      ref.where('tenant_id', '==', tenantId)
        .where('generated_at', '>=', periodStart)
    ).get().toPromise();

    return snapshot.empty ? 0 : snapshot.docs.length;
  }

  // Get voucher statistics for tenant
  async getVoucherStats(tenantId: string): Promise<{
    total: number;
    active: number;
    used: number;
    expired: number;
  }> {
    const snapshot = await this.afs.collection<Voucher>('vouchers', ref =>
      ref.where('tenant_id', '==', tenantId)
    ).get().toPromise();

    const stats = {
      total: 0,
      active: 0,
      used: 0,
      expired: 0
    };

    if (!snapshot.empty) {
      const now = new Date();
      snapshot.docs.forEach(doc => {
        const voucher = doc.data() as Voucher;
        stats.total++;

        const expiresAt = voucher.expires_at instanceof Date
          ? voucher.expires_at
          : new Date((voucher.expires_at as any).seconds * 1000);

        if (voucher.status === 'used') {
          stats.used++;
        } else if (voucher.status === 'expired' || now > expiresAt) {
          stats.expired++;
        } else {
          stats.active++;
        }
      });
    }

    return stats;
  }
}
